import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// CodeMirror-6-basierter Markdown-Editor mit Marker-Toolbar.
//
// Komponenten:
//   - MdEditor          – der reine Editor (controlled via value/onChange)
//   - MarkerToolbar     – Buttons zum Einfügen der Direktiven-Marker
//   - MarkdownEditor    – Default-Export, Toolbar + Editor + Hilfe-Link
//
// Performance-Hinweise:
//   - Editor wird genau einmal aufgebaut (useEffect mit []).
//   - Externe value-Updates triggern dispatch nur wenn current !== value
//     (verhindert Update-Loop bei Tipp-Input).
//   - Custom-Marker-Decorations sind auf visibleRanges beschränkt — Bücher
//     mit 500k Zeichen bleiben damit flüssig.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from "react";
import { EditorState, RangeSetBuilder } from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, WidgetType, highlightActiveLine, keymap, lineNumbers, } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab, } from "@codemirror/commands";
import { search, searchKeymap } from "@codemirror/search";
import { markdown } from "@codemirror/lang-markdown";
import { defaultHighlightStyle, syntaxHighlighting, } from "@codemirror/language";
// ---------- Marker-Decorations -----------------------------------------------
// Regex matcht alle bekannten Direktiven — leaf (::name), container (:::name),
// inline (:name). Wir markieren das gesamte Marker-Token farbig.
// Reihenfolge ist wichtig: ::: muss vor :: vor : matchen.
const MARKER_RE = /(:::(?:keep|fullpage)\b|:::|::pagebreak(?:\{[^}]*\})?|:(?:br|nbsp|nobreak)\b)/g;
function markerKind(raw) {
    if (raw.startsWith(":::keep"))
        return "keep";
    if (raw.startsWith(":::fullpage"))
        return "fullpage";
    if (raw === ":::")
        return "container-end";
    if (raw.startsWith("::pagebreak"))
        return "pagebreak";
    if (raw === ":br")
        return "br";
    if (raw === ":nbsp")
        return "nbsp";
    if (raw === ":nobreak")
        return "nobreak";
    return "other";
}
function buildMarkerDecorations(view) {
    const builder = new RangeSetBuilder();
    for (const { from, to } of view.visibleRanges) {
        const text = view.state.doc.sliceString(from, to);
        MARKER_RE.lastIndex = 0;
        let m;
        while ((m = MARKER_RE.exec(text)) !== null) {
            const start = from + m.index;
            const end = start + m[0].length;
            const kind = markerKind(m[0]);
            builder.add(start, end, Decoration.mark({ class: `cm-md2bp-marker cm-md2bp-${kind}` }));
        }
    }
    return builder.finish();
}
const markerHighlight = ViewPlugin.fromClass(class {
    decorations;
    constructor(view) {
        this.decorations = buildMarkerDecorations(view);
    }
    update(u) {
        if (u.docChanged || u.viewportChanged) {
            this.decorations = buildMarkerDecorations(u.view);
        }
    }
}, { decorations: (v) => v.decorations });
const markerTheme = EditorView.baseTheme({
    ".cm-md2bp-marker": {
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontWeight: "600",
        borderRadius: "3px",
        padding: "0 2px",
    },
    ".cm-md2bp-pagebreak": { color: "#b45309", background: "#fef3c7" },
    ".cm-md2bp-keep, .cm-md2bp-fullpage, .cm-md2bp-container-end": {
        color: "#7c3aed",
        background: "#f3e8ff",
    },
    ".cm-md2bp-br, .cm-md2bp-nbsp, .cm-md2bp-nobreak": {
        color: "#0369a1",
        background: "#e0f2fe",
    },
    ".cm-md2bp-img-embedded": {
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: "0.85em",
        color: "#047857",
        background: "#d1fae5",
        border: "1px solid #6ee7b7",
        borderRadius: "4px",
        padding: "0 6px",
        cursor: "default",
        whiteSpace: "nowrap",
    },
});
// ---------- Eingebettete-Bild-Platzhalter ------------------------------------
// Eingefügte Inhaltsbilder liegen als data:-URL direkt im Markdown
// (![alt](data:image/...;base64,…)). Damit der Editor nicht von hunderten KB
// Base64 zugemüllt wird, ersetzen wir die data:-URL durch einen kompakten Chip
// „🖼 eingebettet · N KB". Der Marker bleibt der echte MD-Inhalt — beim Export
// (Aktuelle MD exportieren) und im Preview wird die volle data:-URL verwendet.
const IMG_DATA_RE = /(!\[[^\]]*\]\()(data:[^)\s]+)(\))/g;
class EmbeddedImgWidget extends WidgetType {
    kb;
    constructor(kb) {
        super();
        this.kb = kb;
    }
    eq(other) {
        return other.kb === this.kb;
    }
    toDOM() {
        const span = document.createElement("span");
        span.className = "cm-md2bp-img-embedded";
        span.textContent = `🖼 eingebettet · ${this.kb} KB`;
        span.title = "Eingebettetes Bild (data-URL) — im Markdown gespeichert";
        return span;
    }
    ignoreEvent() {
        return true;
    }
}
function buildImageDecorations(view) {
    const builder = new RangeSetBuilder();
    const doc = view.state.doc;
    // Nur sichtbare Zeilen scannen (data:-URLs blähen das Doc auf mehrere MB auf —
    // ein voller doc.toString() pro Tastendruck würde das Tippen lähmen). Eine
    // data:-URL liegt immer komplett auf EINER Zeile, daher liefert line.text die
    // ganze URL auch bei nur teilweise sichtbarer Zeile.
    let lastLine = 0;
    for (const { from, to } of view.visibleRanges) {
        const startNo = doc.lineAt(from).number;
        const endNo = doc.lineAt(to).number;
        for (let n = Math.max(startNo, lastLine + 1); n <= endNo; n++) {
            lastLine = n;
            const line = doc.line(n);
            if (line.text.indexOf("](data:") === -1)
                continue;
            IMG_DATA_RE.lastIndex = 0;
            let m;
            while ((m = IMG_DATA_RE.exec(line.text)) !== null) {
                const urlStart = line.from + m.index + m[1].length;
                const urlEnd = urlStart + m[2].length;
                const comma = m[2].indexOf(",");
                const b64Len = comma >= 0 ? m[2].length - comma - 1 : m[2].length;
                const kb = Math.max(1, Math.round((b64Len * 0.75) / 1024));
                builder.add(urlStart, urlEnd, Decoration.replace({ widget: new EmbeddedImgWidget(kb) }));
            }
        }
    }
    return builder.finish();
}
const imagePlaceholder = ViewPlugin.fromClass(class {
    decorations;
    constructor(view) {
        this.decorations = buildImageDecorations(view);
    }
    update(u) {
        if (u.docChanged || u.viewportChanged)
            this.decorations = buildImageDecorations(u.view);
    }
}, {
    decorations: (v) => v.decorations,
    // Ersetzte data:-URL als atomaren Bereich behandeln — Cursor springt drüber,
    // statt mitten im (unsichtbaren) Base64 zu landen.
    provide: (plugin) => EditorView.atomicRanges.of((view) => view.plugin(plugin)?.decorations ?? Decoration.none),
});
/** Liest eine Datei als Data-URL (base64) ein. */
function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error || new Error("FileReader-Fehler"));
        r.readAsDataURL(file);
    });
}
export const MdEditor = forwardRef(function MdEditor({ value, onChange }, ref) {
    const host = useRef(null);
    const view = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const fileInputRef = useRef(null);
    const insertPosRef = useRef(0);
    // Kontextmenü (Rechtsklick): Position auf dem Bildschirm + Doc-Offset.
    const [menu, setMenu] = useState(null);
    useEffect(() => {
        const updateListener = EditorView.updateListener.of((u) => {
            if (u.docChanged)
                onChangeRef.current(u.state.doc.toString());
        });
        const state = EditorState.create({
            doc: value,
            extensions: [
                lineNumbers(),
                highlightActiveLine(),
                history(),
                search({ top: true }),
                markdown(),
                syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
                EditorView.lineWrapping,
                markerHighlight,
                markerTheme,
                imagePlaceholder,
                keymap.of([
                    ...defaultKeymap,
                    ...historyKeymap,
                    ...searchKeymap,
                    indentWithTab,
                ]),
                updateListener,
            ],
        });
        const v = new EditorView({ state, parent: host.current });
        view.current = v;
        return () => {
            v.destroy();
            view.current = null;
        };
        // Editor wird absichtlich nur einmal aufgebaut — value-Sync läuft via Effect unten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Externe value-Updates synchronisieren — nur wenn wirklich anders,
    // sonst Update-Loop bei Tipp-Input.
    useEffect(() => {
        const v = view.current;
        if (!v)
            return;
        const current = v.state.doc.toString();
        if (current === value)
            return;
        v.dispatch({
            changes: { from: 0, to: current.length, insert: value },
        });
    }, [value]);
    // Rechtsklick im Editor → eigenes Kontextmenü („Bild einfügen…").
    useEffect(() => {
        const el = host.current;
        if (!el)
            return;
        const onCtx = (e) => {
            const v = view.current;
            if (!v)
                return;
            e.preventDefault();
            const pos = v.posAtCoords({ x: e.clientX, y: e.clientY }) ??
                v.state.selection.main.head;
            setMenu({ x: e.clientX, y: e.clientY, pos });
        };
        el.addEventListener("contextmenu", onCtx);
        return () => el.removeEventListener("contextmenu", onCtx);
    }, []);
    // Kontextmenü schließen bei Klick außerhalb / Escape / Scroll.
    useEffect(() => {
        if (!menu)
            return;
        const close = () => setMenu(null);
        const onKey = (e) => {
            if (e.key === "Escape")
                setMenu(null);
        };
        window.addEventListener("click", close);
        window.addEventListener("resize", close);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("resize", close);
            window.removeEventListener("keydown", onKey);
        };
    }, [menu]);
    function triggerImageInsert(pos) {
        insertPosRef.current =
            pos ?? view.current?.state.selection.main.head ?? 0;
        setMenu(null);
        fileInputRef.current?.click();
    }
    function chooseImage() {
        triggerImageInsert(menu?.pos);
    }
    async function onImageChosen(e) {
        const file = e.target.files?.[0];
        if (!file)
            return;
        try {
            if (!file.type.startsWith("image/")) {
                alert("Bitte eine Bilddatei wählen (JPG, PNG, WebP, GIF …).");
                return;
            }
            if (file.size > 8 * 1024 * 1024) {
                alert("Bild ist zu groß (>8 MB). Bitte vorher komprimieren — " +
                    "das Bild wird base64-kodiert direkt in die Markdown-Datei eingebettet.");
                return;
            }
            const dataUrl = await readFileAsDataUrl(file);
            const v = view.current;
            if (!v)
                return;
            const pos = Math.min(insertPosRef.current, v.state.doc.length);
            const line = v.state.doc.lineAt(pos);
            // Alt-Text = Dateiname ohne Endung (wird nicht als Bildunterschrift
            // gerendert, nur als alt-Attribut). Bild als eigener Block NACH dem
            // aktuellen Absatz einfügen — Leerzeilen drumherum, sonst zieht Markdown
            // es in den Absatz.
            const alt = file.name.replace(/\.[^.]+$/, "").replace(/[\[\]]/g, "");
            const insert = `\n\n![${alt}](${dataUrl})\n`;
            v.dispatch({
                changes: { from: line.to, insert },
                // Cursor hinter den eingefügten Block setzen (nicht in die data:-URL).
                selection: { anchor: line.to + insert.length },
                scrollIntoView: true,
            });
            v.focus();
        }
        catch (err) {
            alert("Bild konnte nicht eingefügt werden: " + String(err.message));
        }
        finally {
            e.target.value = "";
        }
    }
    useImperativeHandle(ref, () => ({
        insertImage() {
            triggerImageInsert();
        },
        insertAtCursor(snippet, opts) {
            const v = view.current;
            if (!v)
                return;
            const { from, to } = v.state.selection.main;
            // Falls Selection vorhanden und wrapSelection gewünscht: snippet enthält
            // `$SEL` als Platzhalter — wir ersetzen ihn durch den selektierten Text.
            if (opts?.wrapSelection && from !== to && snippet.includes("$SEL")) {
                const selected = v.state.doc.sliceString(from, to);
                const expanded = snippet.replace("$SEL", selected);
                v.dispatch({
                    changes: { from, to, insert: expanded },
                    selection: { anchor: from + expanded.length },
                    scrollIntoView: true,
                });
            }
            else {
                const clean = snippet.replace("$SEL", "");
                v.dispatch({
                    changes: { from, to, insert: clean },
                    selection: { anchor: from + clean.length },
                    scrollIntoView: true,
                });
            }
            v.focus();
        },
        focus() {
            view.current?.focus();
        },
    }));
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: host, className: "md-editor-host" }), _jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", hidden: true, onChange: onImageChosen }), menu && (_jsx("div", { className: "md-ctx-menu", style: { left: menu.x, top: menu.y }, 
                // verhindert, dass der window-Klick-Listener das Menü schließt,
                // bevor der Eintrag-Handler feuert
                onMouseDown: (e) => e.stopPropagation(), children: _jsx("button", { className: "md-ctx-item", onMouseDown: chooseImage, children: "\uD83D\uDDBC Bild einf\u00FCgen\u2026" }) }))] }));
});
const TOOLBAR_BUTTONS = [
    {
        label: "↵ Seite",
        title: "Seitenumbruch — einfach, beliebige Seite (::pagebreak)",
        snippet: "\n\n::pagebreak\n\n",
        kind: "pagebreak",
    },
    {
        label: "↵ Recto",
        title: "Seitenumbruch → rechte Buchseite (::pagebreak{recto})",
        snippet: "\n\n::pagebreak{recto}\n\n",
        kind: "pagebreak",
    },
    {
        label: "↵ Verso",
        title: "Seitenumbruch → linke Buchseite (::pagebreak{verso})",
        snippet: "\n\n::pagebreak{verso}\n\n",
        kind: "pagebreak",
    },
    {
        label: "Vakat",
        title: "Bewusst eine Leerseite einschieben (::pagebreak{blank})",
        snippet: "\n\n::pagebreak{blank}\n\n",
        kind: "pagebreak",
    },
    {
        label: "keep",
        title: "Auswahl als zusammenhaltenden Block umschließen (:::keep)",
        snippet: "\n:::keep\n$SEL\n:::\n",
        wrapSelection: true,
        kind: "container",
    },
    {
        label: "fullpage",
        title: "Auswahl auf eigene Seite (:::fullpage)",
        snippet: "\n:::fullpage\n$SEL\n:::\n",
        wrapSelection: true,
        kind: "container",
    },
    {
        label: ":br",
        title: "Inline-Zeilenumbruch (z. B. in Überschriften)",
        snippet: ":br ",
        kind: "inline",
    },
    {
        label: ":nbsp",
        title: "Geschütztes Leerzeichen (Dr. Müller, 100 km)",
        snippet: ":nbsp ",
        kind: "inline",
    },
    {
        label: ":nobreak",
        title: "Auswahl als nicht-trennbaren Inline-Block",
        snippet: ":nobreak[$SEL]",
        wrapSelection: true,
        kind: "inline",
    },
];
export function MarkerToolbar({ editorRef, onOpenHelp }) {
    function fire(btn) {
        editorRef.current?.insertAtCursor(btn.snippet, {
            wrapSelection: btn.wrapSelection,
        });
    }
    return (_jsxs("div", { className: "md-toolbar", children: [_jsxs("div", { className: "md-toolbar-row", children: [TOOLBAR_BUTTONS.filter((b) => b.kind === "pagebreak").map((b) => (_jsx("button", { className: "md-tb-btn md-tb-pagebreak", title: b.title, onClick: () => fire(b), children: b.label }, b.label))), _jsx("button", { className: "md-tb-btn md-tb-image", title: "Bild an der Cursorposition einf\u00FCgen (wird in die Markdown-Datei eingebettet). Gr\u00F6\u00DFe danach per Klick auf das Bild in der Vorschau \u00E4ndern.", onClick: () => editorRef.current?.insertImage(), children: "\uD83D\uDDBC Bild" })] }), _jsxs("div", { className: "md-toolbar-row", children: [TOOLBAR_BUTTONS.filter((b) => b.kind === "container").map((b) => (_jsx("button", { className: "md-tb-btn md-tb-container", title: b.title, onClick: () => fire(b), children: b.label }, b.label))), TOOLBAR_BUTTONS.filter((b) => b.kind === "inline").map((b) => (_jsx("button", { className: "md-tb-btn md-tb-inline", title: b.title, onClick: () => fire(b), children: b.label }, b.label))), _jsx("span", { className: "md-toolbar-grow" }), _jsx("button", { className: "md-tb-btn md-tb-help", title: "Marker-\u00DCbersicht \u00F6ffnen", onClick: onOpenHelp, children: "?" })] })] }));
}
export default function MarkdownEditor({ value, onChange, onOpenHelp, }) {
    const editorRef = useRef(null);
    return (_jsxs("div", { className: "md-editor-shell", children: [_jsx(MarkerToolbar, { editorRef: editorRef, onOpenHelp: onOpenHelp }), _jsx(MdEditor, { ref: editorRef, value: value, onChange: onChange })] }));
}
// ---------- Cheatsheet -------------------------------------------------------
/** Wird vom EditorScreen als Modal eingehängt. Static, kein eigener State. */
export function MarkerCheatsheet({ onClose }) {
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal md-cheatsheet", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Marker f\u00FCr manuellen Buchsatz" }), _jsx("p", { className: "md-cs-intro", children: "Diese Direktiven kannst du im Markdown verwenden, um Druck-Layout zu steuern. Im Browser-Preview sind die Marker farbig hervorgehoben." }), _jsx("h3", { children: "Seitenumbr\u00FCche" }), _jsx("table", { className: "md-cs-table", children: _jsxs("tbody", { children: [_jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { children: "::pagebreak" }) }), _jsx("td", { children: "Seitenumbruch (irgendeine Seite)" })] }), _jsxs("tr", { children: [_jsx("td", { children: _jsxs("code", { children: ["::pagebreak", "{recto}"] }) }), _jsx("td", { children: "n\u00E4chste rechte Buchseite (Kapitelanfang)" })] }), _jsxs("tr", { children: [_jsx("td", { children: _jsxs("code", { children: ["::pagebreak", "{verso}"] }) }), _jsx("td", { children: "n\u00E4chste linke Buchseite" })] }), _jsxs("tr", { children: [_jsx("td", { children: _jsxs("code", { children: ["::pagebreak", "{blank}"] }) }), _jsx("td", { children: "Leerseite einschieben (z. B. nach Widmung)" })] })] }) }), _jsx("h3", { children: "Inline-Marker" }), _jsx("table", { className: "md-cs-table", children: _jsxs("tbody", { children: [_jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { children: ":br" }) }), _jsxs("td", { children: ["Zeilenumbruch \u2014 z. B. ", _jsx("code", { children: "# Mein:br Buchtitel" })] })] }), _jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { children: ":nbsp" }) }), _jsxs("td", { children: ["gesch\u00FCtztes Leerzeichen \u2014 ", _jsx("code", { children: "Dr.:nbsp M\u00FCller" })] })] }), _jsxs("tr", { children: [_jsx("td", { children: _jsx("code", { children: ":nobreak[\u2026]" }) }), _jsxs("td", { children: ["Inline-Klammer, die nicht trennbar ist \u2014 ", _jsx("code", { children: ":nobreak[H. W. S.]" })] })] })] }) }), _jsx("h3", { children: "Block-Container" }), _jsx("table", { className: "md-cs-table", children: _jsxs("tbody", { children: [_jsxs("tr", { children: [_jsxs("td", { children: [_jsx("code", { children: ":::keep" }), _jsx("br", {}), "\u2026", _jsx("br", {}), _jsx("code", { children: ":::" })] }), _jsx("td", { children: "Block zusammenhalten \u2014 \u00DCberschrift bleibt bei ihrem Folgeabsatz" })] }), _jsxs("tr", { children: [_jsxs("td", { children: [_jsx("code", { children: ":::fullpage" }), _jsx("br", {}), "\u2026", _jsx("br", {}), _jsx("code", { children: ":::" })] }), _jsx("td", { children: "Bild/Block auf eigene Seite stellen" })] })] }) }), _jsx("h3", { children: "Beispiel" }), _jsx("pre", { className: "md-cs-example", children: `::pagebreak{recto}

# Kapitel 3:br Der Beginn

Es war an einem Dienstag im Mai, als Dr.:nbsp Müller den Brief erhielt.

:::keep
## Die Aufgabe

Wie sich später herausstellen sollte, war es ein Wendepunkt.
:::` }), _jsx("div", { className: "bm-actions", children: _jsx("button", { className: "btn primary", onClick: onClose, children: "Schlie\u00DFen" }) })] }) }));
}
